"use client";

import { useReducer, useRef, useEffect, useCallback, useState } from "react";
import type { MapViewerHandle } from "@/features/map/MapViewer";
import type { Waypoint, TravelMode } from "@/features/routebuilder/types";
import {
  buildSegments,
  interpolateAlongSegment,
  estimateCurvature,
  getCameraParams,
  computeRouteBounds,
  computeBearing,
} from "./routeAnimation";
import {
  TourRouteState,
  TourRouteAction,
  TourRouteSettings,
  DEFAULT_TOUR_ROUTE_SETTINGS,
  SETTINGS_STORAGE_KEY,
  TARGET_DURATION_SEC,
  EASE_AVERAGE_FACTOR,
  RouteSegment,
  VehiclePosition,
  INITIAL_TOUR_ROUTE_STATE,
} from "./types";

// ── Settings persistence ──────────────────────────────────────────────────────

function readSettings(): TourRouteSettings {
  if (typeof window === "undefined") return DEFAULT_TOUR_ROUTE_SETTINGS;
  try {
    const raw = localStorage.getItem(SETTINGS_STORAGE_KEY);
    if (!raw) return DEFAULT_TOUR_ROUTE_SETTINGS;
    return { ...DEFAULT_TOUR_ROUTE_SETTINGS, ...JSON.parse(raw) };
  } catch {
    return DEFAULT_TOUR_ROUTE_SETTINGS;
  }
}

function writeSettings(s: TourRouteSettings): void {
  try { localStorage.setItem(SETTINGS_STORAGE_KEY, JSON.stringify(s)); } catch {}
}

// ── Reducer ───────────────────────────────────────────────────────────────────

function reducer(state: TourRouteState, action: TourRouteAction): TourRouteState {
  switch (action.type) {
    case "play":
      return { ...INITIAL_TOUR_ROUTE_STATE, phase: "overview" };

    case "overview_done":
      return { ...state, phase: "driving", lastTimestamp: 0, currentSegmentIndex: 0, metersIntoSegment: 0 };

    // Like overview_done but jumps straight to "arriving" so the car never
    // starts moving before the first panorama is shown.
    // currentSegmentIndex is the segment the car last drove (or is driving).
    // Waypoint 0 is reached without driving anything, so it is -1: viewing_end
    // then advances to segment 0 (photo 1 → photo 2). Starting at 0 here made
    // the car skip photo 2 and drive photo 2 → photo 3 after the first photo.
    case "overview_done_at_first":
      return { ...state, phase: "arriving", lastTimestamp: 0, currentSegmentIndex: -1, metersIntoSegment: 0 };

    case "tick":
      return {
        ...state,
        phase:               "driving",
        metersIntoSegment:   action.metersIntoSegment,
        carPosition:         action.position,
        lastTimestamp:       action.timestamp,
      };

    case "arrive":
      return { ...state, phase: "arriving" };

    case "viewing_start":
      return { ...state, phase: "viewing" };

    case "viewing_end":
      if (!action.hasNext) return { ...state, phase: "complete", carPosition: null };
      return {
        ...state,
        phase:               "driving",
        currentSegmentIndex: state.currentSegmentIndex + 1,
        metersIntoSegment:   0,
        lastTimestamp:       0,
      };

    case "next_segment":
      return {
        ...state,
        phase:               "driving",
        currentSegmentIndex: state.currentSegmentIndex + 1,
        metersIntoSegment:   0,
        lastTimestamp:       0,
      };

    case "pause":
      return { ...state, phase: "paused", pausedFrom: state.phase };

    case "resume":
      // If paused while viewing, go back to viewing; otherwise resume driving.
      if (state.pausedFrom === "viewing" || state.pausedFrom === "arriving")
        return { ...state, phase: state.pausedFrom, pausedFrom: null };
      return { ...state, phase: "driving", lastTimestamp: action.timestamp, pausedFrom: null };

    case "skip": {
      if (!action.hasNext) return { ...state, phase: "complete", carPosition: null };
      return {
        ...state,
        phase:               "driving",
        currentSegmentIndex: state.currentSegmentIndex + 1,
        metersIntoSegment:   0,
        lastTimestamp:       0,
      };
    }

    case "stop":
      return INITIAL_TOUR_ROUTE_STATE;

    case "complete":
      return { ...state, phase: "complete", carPosition: null };

    default:
      return state;
  }
}

// ── Hook ──────────────────────────────────────────────────────────────────────

export interface UseTourRouteOptions {
  waypoints:           Waypoint[];
  routeGeometry:       [number, number][] | null;
  routeDurationSec:    number | null;  // real driving time from routing API
  travelMode:          TravelMode;
  mapHandleRef:        React.RefObject<MapViewerHandle | null>;
  onArriveAtWaypoint:  (waypoint: Waypoint) => void;
  onDismissPanorama:   () => void;
}

export interface RouteStats {
  totalMeters:   number;   // full route length
  currentMeters: number;   // car's cumulative position along the route
  totalSec:      number;   // estimated total driving time at current speed setting
  elapsedSec:    number;   // driving seconds elapsed (pauses excluded)
}

export interface UseTourRouteReturn {
  phase:                import("./types").TourRoutePhase;
  /** Phase we were in before pausing — lets callers tell "paused while viewing" from "paused mid-drive". */
  pausedFrom:           import("./types").TourRoutePhase | null;
  targetWaypointIndex:  number;
  segmentProgress:      number;
  carPosition:          VehiclePosition | null;
  carPositionRef:       React.RefObject<VehiclePosition | null>;
  routeStats:           RouteStats;
  /** Seconds left until auto-advance fires, or null when not applicable (paused, autoAdvance off, not viewing). */
  autoAdvanceRemainingSec: number | null;
  /** True once the next-scene timer was cancelled for this photo (e.g. its trail is playing). */
  autoAdvanceSuspended: boolean;
  /** Cancel the pending next-scene timer for the photo being viewed. */
  suspendAutoAdvance(): void;
  /** Re-arm the next-scene timer with its full duration, counted from now. */
  restartAutoAdvance(): void;
  play():   void;
  playFromViewer(): void;
  pause():  void;
  resume(): void;
  skip():   void;
  stop():   void;
  notifyViewingEnd(): void;
  settings: TourRouteSettings;
  setSettings(s: Partial<TourRouteSettings>): void;
}

export function useTourRoute({
  waypoints,
  routeGeometry,
  routeDurationSec,
  travelMode,
  mapHandleRef,
  onArriveAtWaypoint,
  onDismissPanorama,
}: UseTourRouteOptions): UseTourRouteReturn {
  const [state, dispatch] = useReducer(reducer, INITIAL_TOUR_ROUTE_STATE);
  const [settings, setSettingsState] = useState<TourRouteSettings>(readSettings);
  const [routeStats, setRouteStats] = useState<RouteStats>({
    totalMeters: 0, currentMeters: 0, totalSec: 60, elapsedSec: 0,
  });

  // Refs that the RAF loop reads without closures
  const stateRef           = useRef(state);
  const settingsRef        = useRef(settings);
  const segmentsRef        = useRef<RouteSegment[]>([]);
  const totalRouteLenRef   = useRef(0);         // sum of all segment lengths in metres
  const waypointsRef       = useRef<Waypoint[]>(waypoints);
  const travelModeRef   = useRef<TravelMode>(travelMode); // route-builder mode (kept for fallback)
  const rafIdRef        = useRef(0);
  const autoAdvRef      = useRef<ReturnType<typeof setTimeout> | null>(null);
  const overviewRef     = useRef<ReturnType<typeof setTimeout> | null>(null);
  const arrivalRef      = useRef<ReturnType<typeof setTimeout> | null>(null);
  const elapsedRef           = useRef(0);         // seconds since driving started
  const lastDispatchRef      = useRef(0);         // for 10fps React-state throttle
  const userZoomOffsetRef     = useRef(0);         // accumulated user zoom delta during tour
  const lastSetZoomRef        = useRef<number | null>(null); // zoom we last commanded
  const routeDurationSecRef   = useRef(routeDurationSec);
  const carPositionRef    = useRef<VehiclePosition | null>(null);
  const cameraBearingRef   = useRef(0);    // IIR-smoothed bearing
  const smoothCurvatureRef = useRef(0);   // IIR-smoothed curvature (prevents pitch spikes)
  const cameraLngRef       = useRef(0);   // IIR-smoothed camera center (cinematic lag)
  const cameraLatRef       = useRef(0);
  const viewingStartedAtRef = useRef(0);  // performance.now() when the current photo's auto-advance began
  const [autoAdvanceRemainingSec, setAutoAdvanceRemainingSec] = useState<number | null>(null);
  const [autoAdvanceSuspended, setAutoAdvanceSuspended] = useState(false);
  const autoAdvSuspendedRef = useRef(false);

  // Keep refs in sync
  useEffect(() => { stateRef.current          = state;            }, [state]);
  useEffect(() => { settingsRef.current       = settings;         }, [settings]);
  useEffect(() => { waypointsRef.current      = waypoints;        }, [waypoints]);
  useEffect(() => { travelModeRef.current     = travelMode;       }, [travelMode]);
  useEffect(() => { routeDurationSecRef.current = routeDurationSec; }, [routeDurationSec]);

  // Recompute segments whenever waypoints or geometry changes
  useEffect(() => {
    const segs = buildSegments(waypoints, routeGeometry);
    segmentsRef.current  = segs;
    totalRouteLenRef.current = segs.reduce((sum, s) => sum + s.lengthMeters, 0);
  }, [waypoints, routeGeometry]);

  // Mark when the current photo started being viewed — resets on every
  // transition into "viewing" (first arrival, next photo, or resume-from-pause).
  useEffect(() => {
    if (state.phase === "viewing") viewingStartedAtRef.current = performance.now();
    // A new photo (or any non-viewing phase) starts with the timer armed again.
    if (state.phase !== "viewing" && state.phase !== "paused") {
      autoAdvSuspendedRef.current = false;
      setAutoAdvanceSuspended(false);
    }
  }, [state.phase]);

  // Countdown shown on the photo (Task 23) — ticks while auto-advance is armed.
  useEffect(() => {
    if (state.phase !== "viewing" || settings.autoAdvance === "off" || autoAdvanceSuspended) {
      setAutoAdvanceRemainingSec(null);
      return;
    }
    const durationMs = settings.autoAdvance * 1000;
    const tick = () => {
      const remainingMs = durationMs - (performance.now() - viewingStartedAtRef.current);
      setAutoAdvanceRemainingSec(Math.max(0, Math.ceil(remainingMs / 1000)));
    };
    tick();
    const interval = setInterval(tick, 200);
    return () => clearInterval(interval);
  }, [state.phase, settings.autoAdvance, autoAdvanceSuspended]);

  // ── Helpers ───────────────────────────────────────────────────────────────

  const clearTimers = useCallback(() => {
    if (autoAdvRef.current)  { clearTimeout(autoAdvRef.current);  autoAdvRef.current  = null; }
    if (overviewRef.current) { clearTimeout(overviewRef.current); overviewRef.current = null; }
    if (arrivalRef.current)  { clearTimeout(arrivalRef.current);  arrivalRef.current  = null; }
    if (rafIdRef.current)    { cancelAnimationFrame(rafIdRef.current); rafIdRef.current = 0; }
  }, []);

  const getRawMap = useCallback((): any | null => {
    const handle = mapHandleRef.current as any;
    return handle?.getRawMap?.() ?? null;
  }, [mapHandleRef]);

  const setCameraSmooth = useCallback((params: import("./types").CameraParams) => {
    const handle = mapHandleRef.current as any;
    handle?.setCameraSmooth?.(params);
  }, [mapHandleRef]);

  // ── Route overview zoom ───────────────────────────────────────────────────

  const zoomToRouteOverview = useCallback(() => {
    const map  = getRawMap();
    const segs = segmentsRef.current;
    if (!map || !segs.length) return;
    const bounds  = computeRouteBounds(segs);
    const canvas  = map.getCanvas();
    const padding = Math.round(Math.min(canvas.clientWidth, canvas.clientHeight) * 0.1);
    map.fitBounds(
      [[bounds[0], bounds[1]], [bounds[2], bounds[3]]],
      { padding, pitch: 0, bearing: 0, duration: 1400 },
    );
  }, [getRawMap]);

  // ── Arrival sequence ──────────────────────────────────────────────────────

  // Called after a panorama is dismissed (auto-advance or manual close).
  // If showRouting is off, skip the driving phase and go straight to the next arrival.
  const advanceAfterViewing = useCallback((segIndex: number) => {
    const segs    = segmentsRef.current;
    const hasNext = segIndex + 1 < segs.length;
    elapsedRef.current = 0;

    if (!hasNext) {
      dispatch({ type: "viewing_end", hasNext: false });
      zoomToRouteOverview();
      return;
    }

    if (!settingsRef.current.showRouting) {
      // No routing animation — teleport camera to next waypoint and open its panorama
      const nextWp = waypointsRef.current[segIndex + 2] ?? waypointsRef.current[waypointsRef.current.length - 1];
      const map    = getRawMap();
      if (map) {
        map.easeTo({
          center:   [nextWp.lng, nextWp.lat],
          zoom:     17,
          pitch:    65,
          duration: 800,
          easing:   (t: number) => 1 - Math.pow(1 - t, 3),
        });
      }
      // Dispatch viewing_end so the segment index advances, then immediately arrive
      dispatch({ type: "viewing_end", hasNext: true });
      arrivalRef.current = setTimeout(() => {
        dispatch({ type: "arrive" });
        arrivalRef.current = setTimeout(() => {
          dispatch({ type: "viewing_start" });
          onArriveAtWaypoint(nextWp);
          const adv = settingsRef.current.autoAdvance;
          if (adv !== "off") {
            autoAdvRef.current = setTimeout(() => {
              onDismissPanorama();
              advanceAfterViewing(segIndex + 1);
            }, adv * 1000);
          }
        }, 900);
      }, 50);
      return;
    }

    dispatch({ type: "viewing_end", hasNext: true });
  }, [getRawMap, onArriveAtWaypoint, onDismissPanorama, zoomToRouteOverview]);

  const handleArrival = useCallback((segIndex: number) => {
    dispatch({ type: "arrive" });

    const wps  = waypointsRef.current;
    const wp   = wps[segIndex + 1] ?? wps[wps.length - 1]; // toWaypoint
    const map  = getRawMap();

    // Dramatic zoom-in brake
    if (map) {
      map.easeTo({
        center:   [wp.lng, wp.lat],
        zoom:     17,
        pitch:    65,
        duration: 600,
        easing:   (t: number) => 1 - Math.pow(1 - t, 3),
      });
    }

    // Arrival easeTo changes zoom outside our tick — clear the last-set reference
    // so the next segment doesn't mistake it for user zoom input.
    lastSetZoomRef.current = null;
    userZoomOffsetRef.current = 0;

    arrivalRef.current = setTimeout(() => {
      dispatch({ type: "viewing_start" });
      onArriveAtWaypoint(wp);

      // Auto-advance timer
      const adv = settingsRef.current.autoAdvance;
      if (adv !== "off") {
        autoAdvRef.current = setTimeout(() => {
          onDismissPanorama();
          advanceAfterViewing(segIndex);
        }, adv * 1000);
      }
    }, 700);
  }, [getRawMap, onArriveAtWaypoint, onDismissPanorama, advanceAfterViewing]);

  // ── RAF tick ──────────────────────────────────────────────────────────────

  const tick = useCallback((timestamp: number) => {
    const s    = stateRef.current;
    const segs = segmentsRef.current;

    // ── Driving phase ─────────────────────────────────────────────────────────
    if (s.phase !== "driving") { rafIdRef.current = 0; return; }

    const seg = segs[s.currentSegmentIndex];
    if (!seg) { dispatch({ type: "complete" }); return; }

    // Routing animation turned off mid-drive — don't leave the car frozen
    // here forever; jump straight to this segment's destination as if it had
    // finished driving, same as a completed segment. Every segment after
    // this one already skips the drive entirely (see advanceAfterViewing's
    // !showRouting branch), so this just closes the gap for the current one.
    if (!settingsRef.current.showRouting) {
      const pos = interpolateAlongSegment(seg, seg.lengthMeters, s.carPosition?.bearing);
      carPositionRef.current = pos;
      cancelAnimationFrame(rafIdRef.current);
      rafIdRef.current = 0;
      handleArrival(s.currentSegmentIndex);
      return;
    }

    // First frame of a new segment (or after overview): re-seed IIR filters so
    // the camera doesn't inherit stale bearing/position from the previous run.
    if (s.lastTimestamp === 0 && seg.coords.length >= 2) {
      cameraBearingRef.current  = computeBearing(seg.coords[0], seg.coords[1]);
      cameraLngRef.current      = seg.coords[0][0];
      cameraLatRef.current      = seg.coords[0][1];
      smoothCurvatureRef.current = 0;
    }

    // Delta time — clamp to 50ms to handle tab-switch spikes
    const lastTs  = s.lastTimestamp || timestamp;
    const deltaMs = Math.min(timestamp - lastTs, 50);

    // Speed = totalRouteLength / (targetDuration * easeAverageFactor)
    const totalLen  = totalRouteLenRef.current || 1;
    const targetSec = TARGET_DURATION_SEC[settingsRef.current.speed];
    const baseMps   = totalLen / (targetSec * EASE_AVERAGE_FACTOR);

    // Longer legs drive faster, shorter legs slower — relative to this route's
    // own average leg length, so the overall trip still lands near targetSec.
    // sqrt keeps the effect noticeable without being extreme; clamped so no
    // leg crawls or rockets off disproportionately.
    const avgSegLen     = segs.length > 0 ? totalLen / segs.length : seg.lengthMeters;
    const lengthFactor  = Math.max(0.5, Math.min(2, Math.sqrt(seg.lengthMeters / Math.max(avgSegLen, 1))));
    const segMps        = baseMps * lengthFactor;

    // Ease-in/ease-out: f(t) = 0.3 + 0.7·sin(π·t) over [0,1] within the segment.
    const t      = seg.lengthMeters > 0 ? s.metersIntoSegment / seg.lengthMeters : 0;
    const eased  = 0.3 + 0.7 * Math.sin(Math.PI * Math.max(0, Math.min(1, t)));
    const advance    = (deltaMs / 1000) * segMps * eased;
    const newMeters  = s.metersIntoSegment + advance;

    elapsedRef.current += deltaMs / 1000;

    if (newMeters >= seg.lengthMeters) {
      const pos = interpolateAlongSegment(seg, seg.lengthMeters, s.carPosition?.bearing);
      carPositionRef.current = pos;
      cancelAnimationFrame(rafIdRef.current);
      rafIdRef.current = 0;
      handleArrival(s.currentSegmentIndex);
      return;
    }

    const pos          = interpolateAlongSegment(seg, newMeters, s.carPosition?.bearing);
    const rawCurvature = estimateCurvature(seg, newMeters);

    // ── Smooth curvature (tau = 1.5 s) ───────────────────────────────────────
    const cAlpha = 1 - Math.exp(-(deltaMs / 1000) / 1.5);
    smoothCurvatureRef.current += (rawCurvature - smoothCurvatureRef.current) * cAlpha;
    const curvature = smoothCurvatureRef.current;

    // ── Smooth bearing (tau = 1.4 s, short arc) ──────────────────────────────
    // Longer tau = slower camera rotation response = ease-in/out feel on turns.
    const bDelta = ((pos.bearing - cameraBearingRef.current) % 360 + 540) % 360 - 180;
    const bAlpha = 1 - Math.exp(-(deltaMs / 1000) / 1.4);
    cameraBearingRef.current = ((cameraBearingRef.current + bDelta * bAlpha) + 360) % 360;

    // ── Smooth camera center ─────────────────────────────────────────────────
    // Cinematic: aim 120 m ahead with 0.3 s lag → camera floats ahead of car.
    // Follow: small 0.15 s lag — feels like a camera operator, not a fixed rig.
    // Both create natural ease-in/out when the car changes direction or speed.
    const isCinematic = settingsRef.current.cameraMode === "cinematic";
    const targetLng   = isCinematic
      ? pos.lng + Math.sin(cameraBearingRef.current * (Math.PI / 180)) * 0.0012
      : pos.lng;
    const targetLat   = isCinematic
      ? pos.lat + Math.cos(cameraBearingRef.current * (Math.PI / 180)) * 0.0012
      : pos.lat;
    const centerTau = isCinematic ? 0.3 : 0.15;
    const pAlpha    = 1 - Math.exp(-(deltaMs / 1000) / centerTau);
    cameraLngRef.current += (targetLng - cameraLngRef.current) * pAlpha;
    cameraLatRef.current += (targetLat - cameraLatRef.current) * pAlpha;

    const smoothedPos: VehiclePosition = { ...pos, bearing: cameraBearingRef.current };

    const camera = getCameraParams(
      smoothedPos,
      { lng: cameraLngRef.current, lat: cameraLatRef.current },
      seg, newMeters,
      settingsRef.current.cameraMode,
      settingsRef.current.travelMode,
      curvature,
      elapsedRef.current,
    );

    // ── User zoom offset ──────────────────────────────────────────────────────
    // Detect manual zoom: if the map's actual zoom differs from what we last set,
    // the user scrolled/pinched — accumulate that delta as a persistent offset.
    const rawMap = getRawMap();
    if (rawMap && lastSetZoomRef.current !== null) {
      const actualZoom = rawMap.getZoom();
      const userDelta  = actualZoom - lastSetZoomRef.current;
      if (Math.abs(userDelta) > 0.02) {
        userZoomOffsetRef.current += userDelta;
      }
    }
    camera.zoom += userZoomOffsetRef.current;
    lastSetZoomRef.current = camera.zoom;

    setCameraSmooth(camera);
    carPositionRef.current = smoothedPos;

    // Throttled React state dispatch (10fps)
    if (timestamp - lastDispatchRef.current > 100) {
      dispatch({ type: "tick", position: smoothedPos, metersIntoSegment: newMeters, timestamp });
      // Cumulative meters = sum of all completed segments + current progress
      const completedMeters = segs
        .slice(0, s.currentSegmentIndex)
        .reduce((sum, sg) => sum + sg.lengthMeters, 0);
      const totalLen    = totalRouteLenRef.current || 1;
      const totalGpsSec = routeDurationSecRef.current ?? 0;
      const currentMeters = completedMeters + newMeters;
      setRouteStats({
        totalMeters:   totalLen,
        currentMeters,
        totalSec:      totalGpsSec,
        elapsedSec:    (currentMeters / totalLen) * totalGpsSec,
      });
      lastDispatchRef.current = timestamp;
    }

    // Keep MapLibre rendering continuously so map.on('render', ...) fires every
    // frame for the car icon — without this, MapLibre may go idle between ticks.
    getRawMap()?.triggerRepaint();

    rafIdRef.current = requestAnimationFrame(tick);
  }, [handleArrival, setCameraSmooth, getRawMap]);

  // Start/restart RAF only when driving
  useEffect(() => {
    if (state.phase === "driving" && rafIdRef.current === 0) {
      rafIdRef.current = requestAnimationFrame(tick);
    }
    if (state.phase !== "driving" && rafIdRef.current !== 0) {
      cancelAnimationFrame(rafIdRef.current);
      rafIdRef.current = 0;
    }
  }, [state.phase, tick]);

  // ── Public actions ────────────────────────────────────────────────────────

  const play = useCallback(() => {
    clearTimers();
    elapsedRef.current           = 0;
    smoothCurvatureRef.current   = 0;
    userZoomOffsetRef.current    = 0;
    lastSetZoomRef.current       = null;

    // Seed IIR filters from the first road vertex
    const segs = segmentsRef.current;
    if (segs.length > 0 && segs[0].coords.length >= 2) {
      cameraBearingRef.current = computeBearing(segs[0].coords[0], segs[0].coords[1]);
      cameraLngRef.current     = segs[0].coords[0][0];
      cameraLatRef.current     = segs[0].coords[0][1];
    } else {
      cameraBearingRef.current = 0;
      cameraLngRef.current     = 0;
      cameraLatRef.current     = 0;
    }

    dispatch({ type: "play" });

    if (segs.length === 0) {
      dispatch({ type: "overview_done" });
      return;
    }

    const map = getRawMap();
    if (!map) {
      dispatch({ type: "overview_done" });
      return;
    }

    if (!settingsRef.current.showRouting) {
      // No routing animation — fly straight to first waypoint and open its panorama
      const firstWp = waypointsRef.current[0];
      if (firstWp) {
        map.easeTo({
          center:   [firstWp.lng, firstWp.lat],
          zoom:     17,
          pitch:    65,
          duration: 1200,
          easing:   (t: number) => 1 - Math.pow(1 - t, 3),
        });
      }
      overviewRef.current = setTimeout(() => {
        dispatch({ type: "overview_done_at_first" });
        // With showRouting off there is no driving — go straight to the first
        // arrival (index -1: nothing driven yet, see the reducer).
        setTimeout(() => {
          if (firstWp) {
            dispatch({ type: "arrive" });
            arrivalRef.current = setTimeout(() => {
              dispatch({ type: "viewing_start" });
              onArriveAtWaypoint(firstWp);
              const adv = settingsRef.current.autoAdvance;
              if (adv !== "off") {
                autoAdvRef.current = setTimeout(() => {
                  onDismissPanorama();
                  advanceAfterViewing(-1);
                }, adv * 1000);
              }
            }, 700);
          }
        }, 50);
      }, 1300);
      return;
    }

    // Step 1: fit the whole route into 80% of the viewport (pitch 0, top-down)
    const bounds  = computeRouteBounds(segs);
    const canvas  = map.getCanvas();
    const padding = Math.round(Math.min(canvas.clientWidth, canvas.clientHeight) * 0.1);
    map.fitBounds(
      [[bounds[0], bounds[1]], [bounds[2], bounds[3]]],
      { padding, pitch: 0, bearing: 0, duration: 900 },
    );

    // Step 2: after fitBounds settles, animate into the starting camera position
    // for the chosen camera mode so the transition feels intentional.
    const firstSeg     = segs[0];
    const startCoord   = firstSeg.coords[0];
    const startBearing = firstSeg.coords.length >= 2
      ? computeBearing(firstSeg.coords[0], firstSeg.coords[1])
      : 0;

    const cameraMode = settingsRef.current.cameraMode;

    let zoomTarget: number;
    let pitchTarget: number;
    let bearingTarget: number;
    let centerTarget: [number, number];

    if (cameraMode === "overview") {
      const lengthKm = firstSeg.lengthMeters / 1000;
      const mid = interpolateAlongSegment(firstSeg, firstSeg.lengthMeters / 2);
      zoomTarget    = Math.max(10, Math.min(16, 13 + Math.log2(9.8 / Math.max(lengthKm, 0.1))));
      pitchTarget   = 20;
      bearingTarget = 0;
      centerTarget  = [mid.lng, mid.lat];
    } else {
      zoomTarget    = 15;
      pitchTarget   = cameraMode === "cinematic" ? 55 : 45;
      bearingTarget = startBearing;
      centerTarget  = startCoord;
    }

    // Hold the overview (user-configurable, default 5 s), then zoom into the
    // first waypoint and open its panorama. Driving to waypoint 1 only starts
    // after the user (or auto-advance) dismisses.
    const overviewHoldMs = settingsRef.current.overviewDurationSec * 1000;
    overviewRef.current = setTimeout(() => {
      const firstWp = waypointsRef.current[0];

      // Zoom into the first waypoint at street level
      map.easeTo({
        center:   firstWp ? [firstWp.lng, firstWp.lat] : centerTarget,
        zoom:     18,
        pitch:    65,
        bearing:  bearingTarget,
        duration: 1400,
        easing:   (t: number) => 1 - Math.pow(1 - t, 3),
      });

      // After zoom settles, go directly to "arriving" (skips "driving" so the
      // car never moves before the panorama opens) then open the first panorama.
      overviewRef.current = setTimeout(() => {
        dispatch({ type: "overview_done_at_first" });

        if (firstWp) {
          arrivalRef.current = setTimeout(() => {
            dispatch({ type: "viewing_start" });
            onArriveAtWaypoint(firstWp);
            const adv = settingsRef.current.autoAdvance;
            if (adv !== "off") {
              autoAdvRef.current = setTimeout(() => {
                onDismissPanorama();
                advanceAfterViewing(-1);
              }, adv * 1000);
            }
          }, 700);
        }
      }, 1500);
    }, overviewHoldMs);
  }, [clearTimers, getRawMap, onArriveAtWaypoint, onDismissPanorama, advanceAfterViewing]);

  const pause = useCallback(() => {
    const phase = stateRef.current.phase;
    if (phase !== "driving" && phase !== "viewing" && phase !== "arriving") return;
    // Must cancel every pending timer, not just RAF/auto-advance — an
    // in-flight `arrivalRef` timeout (from handleArrival/advanceAfterViewing)
    // would otherwise keep running during the pause, dispatch viewing_start
    // behind our back, and leave `resume()` with no "paused" phase to resume
    // from (the bug: pause → change settings → resume gets stuck).
    clearTimers();
    dispatch({ type: "pause" });
  }, [clearTimers]);

  const resume = useCallback(() => {
    const s = stateRef.current;
    if (s.phase !== "paused") return;
    dispatch({ type: "resume", timestamp: performance.now() });

    // "driving" resumes for free — the RAF-restart effect below picks it up.
    // "viewing"/"arriving" were advanced by setTimeouts that pause() just
    // cancelled, so resume must re-arm whichever one was in flight.
    if (s.pausedFrom === "viewing") {
      const adv = settingsRef.current.autoAdvance;
      if (adv !== "off" && !autoAdvSuspendedRef.current) {
        autoAdvRef.current = setTimeout(() => {
          onDismissPanorama();
          advanceAfterViewing(s.currentSegmentIndex);
        }, adv * 1000);
      }
    } else if (s.pausedFrom === "arriving") {
      const wps = waypointsRef.current;
      const wp  = wps[s.currentSegmentIndex + 1] ?? wps[wps.length - 1];
      arrivalRef.current = setTimeout(() => {
        dispatch({ type: "viewing_start" });
        onArriveAtWaypoint(wp);
        const adv = settingsRef.current.autoAdvance;
        if (adv !== "off") {
          autoAdvRef.current = setTimeout(() => {
            onDismissPanorama();
            advanceAfterViewing(s.currentSegmentIndex);
          }, adv * 1000);
        }
      }, 700);
    }
  }, [advanceAfterViewing, onArriveAtWaypoint, onDismissPanorama]);

  const skip = useCallback(() => {
    clearTimers();
    onDismissPanorama();
    const segs    = segmentsRef.current;
    const hasNext = stateRef.current.currentSegmentIndex + 1 < segs.length;
    elapsedRef.current = 0;
    dispatch({ type: "skip", hasNext });
  }, [clearTimers, onDismissPanorama]);

  const stop = useCallback(() => {
    clearTimers();
    onDismissPanorama();
    carPositionRef.current = null;
    dispatch({ type: "stop" });
  }, [clearTimers, onDismissPanorama]);

  const setSettings = useCallback((partial: Partial<TourRouteSettings>) => {
    setSettingsState((prev) => {
      const next = { ...prev, ...partial };
      writeSettings(next);
      return next;
    });
  }, []);

  // ── Called when user presses Play Route while already inside the first photo ─
  // The first photo's panorama is already open, so this only does the camera
  // choreography — zoom out to the whole track, then back in on waypoint 1 —
  // before settling into "viewing" at segment 0 (NOT "driving": dispatching
  // "overview_done" here was the bug that made the car drive off immediately
  // and arrive at waypoint 2, i.e. "skip to the second image"). Dismissing the
  // still-open first photo is what triggers notifyViewingEnd → advanceAfterViewing(-1)
  // → segment 0 → the car actually starts driving toward waypoint 2.
  const playFromViewer = useCallback(() => {
    clearTimers();
    elapsedRef.current         = 0;
    smoothCurvatureRef.current = 0;
    userZoomOffsetRef.current  = 0;
    lastSetZoomRef.current     = null;

    const segs = segmentsRef.current;
    if (segs.length > 0 && segs[0].coords.length >= 2) {
      cameraBearingRef.current = computeBearing(segs[0].coords[0], segs[0].coords[1]);
      cameraLngRef.current     = segs[0].coords[0][0];
      cameraLatRef.current     = segs[0].coords[0][1];
    }

    // Enter the "overview" phase up front. This is the fix for the intro not
    // showing: "overview" is NOT in page.tsx's showPanorama set, so the
    // fullscreen first-photo overlay unmounts and the map camera animation
    // below is actually visible. (Staying in "idle" kept the photo on top,
    // hiding the whole zoom-out/zoom-in beat.) It also resets currentSegmentIndex
    // to 0 so the tour always starts from waypoint 0.
    dispatch({ type: "play" });

    // Re-open the first waypoint's photo and arm auto-advance — mirrors play()'s
    // first-waypoint handoff. Goes through "arriving" (never "driving"), so the
    // car doesn't drive off to waypoint 2 before photo 0 is shown again.
    const enterFirstViewing = () => {
      const firstWp = waypointsRef.current[0];
      dispatch({ type: "overview_done_at_first" }); // → "arriving"
      arrivalRef.current = setTimeout(() => {
        dispatch({ type: "viewing_start" });
        if (firstWp) onArriveAtWaypoint(firstWp);
        const adv = settingsRef.current.autoAdvance;
        if (adv !== "off") {
          autoAdvRef.current = setTimeout(() => {
            onDismissPanorama();
            advanceAfterViewing(-1);
          }, adv * 1000);
        }
      }, 700);
    };

    if (segs.length === 0) {
      enterFirstViewing();
      return;
    }

    const runOverview = (map: any) => {
      // Same overview beat as play(): fit the whole track, hold, then zoom
      // back into the first waypoint.
      const bounds  = computeRouteBounds(segs);
      const canvas  = map.getCanvas();
      const padding = Math.round(Math.min(canvas.clientWidth, canvas.clientHeight) * 0.1);
      map.fitBounds(
        [[bounds[0], bounds[1]], [bounds[2], bounds[3]]],
        { padding, pitch: 0, bearing: 0, duration: 900 },
      );

      const firstWp = waypointsRef.current[0];
      overviewRef.current = setTimeout(() => {
        map.easeTo({
          center:   firstWp ? [firstWp.lng, firstWp.lat] : segs[0].coords[0],
          zoom:     18,
          pitch:    65,
          bearing:  cameraBearingRef.current,
          duration: 1400,
          easing:   (t: number) => 1 - Math.pow(1 - t, 3),
        });

        overviewRef.current = setTimeout(enterFirstViewing, 1500);
      }, settingsRef.current.overviewDurationSec * 1000);
    };

    // The underlying MapLibre instance can still be loading its style/tiles
    // right after the tour page mounts (page.tsx itself polls for this same
    // reason) — retry briefly instead of silently skipping the whole intro
    // animation just because the map wasn't ready on the very first check.
    let attempts = 0;
    const tryStart = () => {
      const map = getRawMap();
      if (map) { runOverview(map); return; }
      if (attempts++ < 15) {
        overviewRef.current = setTimeout(tryStart, 200);
      } else {
        enterFirstViewing();
      }
    };
    tryStart();
  }, [clearTimers, getRawMap, onArriveAtWaypoint, onDismissPanorama, advanceAfterViewing]);

  // ── Called by page.tsx when user manually closes panorama (no auto-advance) ─

  // Expose a stable callback so page.tsx can call it after viewer closes —
  // also allowed while paused from viewing (e.g. the Skip button, which only
  // shows then), so a skip-while-paused isn't silently swallowed here.
  const onViewingEnd = useCallback(() => {
    const s = stateRef.current;
    const isViewingOrPausedFromViewing =
      s.phase === "viewing" || (s.phase === "paused" && s.pausedFrom === "viewing");
    if (!isViewingOrPausedFromViewing) return;
    if (autoAdvRef.current) { clearTimeout(autoAdvRef.current); autoAdvRef.current = null; }
    advanceAfterViewing(s.currentSegmentIndex);
  }, [advanceAfterViewing]);

  const suspendAutoAdvance = useCallback(() => {
    if (stateRef.current.phase !== "viewing") return;
    if (autoAdvRef.current) { clearTimeout(autoAdvRef.current); autoAdvRef.current = null; }
    autoAdvSuspendedRef.current = true;
    setAutoAdvanceSuspended(true);
  }, []);

  // The timer normally starts the moment a photo opens; the tour page calls this
  // once the image and its trail have actually loaded so slow loading doesn't
  // eat into the viewing time.
  const restartAutoAdvance = useCallback(() => {
    const s = stateRef.current;
    if (s.phase !== "viewing") return;
    const adv = settingsRef.current.autoAdvance;
    if (autoAdvRef.current) { clearTimeout(autoAdvRef.current); autoAdvRef.current = null; }
    autoAdvSuspendedRef.current = false;
    setAutoAdvanceSuspended(false);
    viewingStartedAtRef.current = performance.now();
    if (adv === "off") return;
    autoAdvRef.current = setTimeout(() => {
      onDismissPanorama();
      advanceAfterViewing(s.currentSegmentIndex);
    }, adv * 1000);
  }, [advanceAfterViewing, onDismissPanorama]);

  // Cleanup on unmount
  useEffect(() => () => clearTimers(), [clearTimers]);

  return {
    phase:               state.phase,
    targetWaypointIndex: state.currentSegmentIndex + 1,
    segmentProgress:     segmentsRef.current[state.currentSegmentIndex]
      ? state.metersIntoSegment / (segmentsRef.current[state.currentSegmentIndex].lengthMeters || 1)
      : 0,
    carPosition:  state.carPosition,
    carPositionRef,
    routeStats,
    autoAdvanceRemainingSec,
    autoAdvanceSuspended,
    suspendAutoAdvance,
    restartAutoAdvance,
    pausedFrom: state.pausedFrom,
    play, playFromViewer, pause, resume, skip, stop,
    notifyViewingEnd: onViewingEnd,
    settings, setSettings,
  };
}
